/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { PureComponent, ChangeEvent, ReactEventHandler } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import {
  Banner,
  AppBanner,
  AppBannerLayout,
  Type,
  FormInput,
} from '@spotify-internal/encore-creator-web';
import tokens from '@spotify-internal/encore-foundation';
import { Link, Redirect } from 'react-router-dom';
import { groupBy, debounce } from 'lodash';
import {
  LOADING,
  ERROR,
  selectNetworkListStatus,
  selectNetworkList,
  selectNetworkSearchResults,
  selectCurrentNetworkId,
  selectMeBang,
} from '../../../store/selectors';
import { loadNetworkList, searchNetworks } from '../../../store/networkList';
import { Me, ReduxState, NetworkListResult } from '../../../types';

type Props = {
  me: Me;
  status?: 'LOADING' | 'ERROR';
  networks?: NetworkListResult[];
  searchResults?: {
    results: NetworkListResult[];
    filter: string;
  };
  loadNetworkList: typeof loadNetworkList;
  searchNetworks: typeof searchNetworks;
};

type State = {
  letter: string;
  filter: string;
};

export class NetworkList extends PureComponent<Props, State> {
  state = { letter: 'a', filter: '' };

  UNSAFE_componentWillMount() {
    if (this.props.searchResults) this.setState({ filter: this.props.searchResults.filter });
  }

  componentDidMount() {
    if (this.props.me.employee) this.props.loadNetworkList();
  }

  get networkList() {
    return groupBy(this.props.networks, item => {
      const first = item.name[0];
      return /[a-zA-Z]/.test(first) ? first.toLowerCase() : '#';
    });
  }

  handleLetterClick = (letter: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    this.setState({ letter });
  };

  debouncedSearch = debounce(this.props.searchNetworks, 400);

  handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    this.setState({ filter: e.target.value });
    /* istanbul ignore else */
    if (e.target.value) this.debouncedSearch(e.target.value);
  };

  renderSearchFormAndResults() {
    const { searchResults } = this.props;
    let results = searchResults ? searchResults.results : [];
    /* istanbul ignore if */
    if (!this.state.filter) results = [];

    return (
      <div style={{ paddingTop: '20px', width: '650px' }}>
        <Type as="h1" color="salmon" variant={Type.heading2} style={{ marginTop: tokens.spacer32 }}>
          Show Search
        </Type>
        <FormInput
          type="text"
          value={this.state.filter}
          onChange={this.handleOnChange}
          placeholder="Search for a podcast..."
        />
        <div data-qa="search-results">
          {results.map(show => (
            <div
              key={`${show.name}-${show.id}`}
              style={{ display: 'flex', justifyContent: 'space-between' }}
            >
              <Link to={`/podcast/${show.id}`}>{show.name}</Link>
              <p>{show.id}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  render() {
    if (this.props.status === LOADING) {
      return <span data-qa="network-list-loading" />;
    }

    if (this.props.status === ERROR) {
      return <span data-qa="network-list-error" />;
    }

    if (!this.props.me.employee) {
      return <Redirect to="/gateway" />;
    }

    if (!this.props.networks) {
      return null;
    }

    return (
      <div data-qa="network-list">
        <div>
          <AppBanner>
            <AppBannerLayout>
              <Banner variant={Banner.announcement}>
                You have employee access (read-only), you cannot make changes in Spotify for
                Podcasters and any podcasts submitted will not be published. All data is
                confidential.
                <br />
                For any questions, please reach out to{' '}
                <a href="https://spotify.slack.com/archives/C66FGNE4A">
                  #spotify-for-podcasters
                </a>{' '}
                on Slack
              </Banner>
            </AppBannerLayout>
          </AppBanner>

          {this.renderSearchFormAndResults()}

          <Type
            as="h1"
            color="salmon"
            variant={Type.heading2}
            style={{ marginTop: tokens.spacer32 }}
          >
            Network List
          </Type>
          {/* eslint-disable jsx-a11y/anchor-is-valid */}
          {Object.keys(this.networkList)
            .sort()
            .map(key => (
              <a
                onClick={this.handleLetterClick(key)}
                style={{ marginRight: '8px', textDecoration: 'underline', fontSize: '18px' }}
                key={`#${key}`}
                id={`${key}`}
              >
                {key.toUpperCase()}
              </a>
            ))}
          {/* eslint-enable jsx-a11y/anchor-is-valid */}
        </div>
        <section style={{ paddingTop: '20px', width: '650px' }}>
          {this.networkList[this.state.letter].map(network => (
            <div
              key={`${network.name}-key+${Math.random()}`}
              style={{ display: 'flex', justifyContent: 'space-between' }}
            >
              <Link to={`/network/${network.id}`} id={network.id}>
                {network.name}
              </Link>
              <p>{network.id}</p>
            </div>
          ))}
        </section>
      </div>
    );
  }
}

const selectors = createStructuredSelector<
  ReduxState,
  {
    status: ReturnType<typeof selectNetworkListStatus>;
    networks: ReturnType<typeof selectNetworkList>;
    me: ReturnType<typeof selectMeBang>;
    searchResults: ReturnType<typeof selectNetworkSearchResults>;
    currentNetworkId: ReturnType<typeof selectCurrentNetworkId>;
  }
>({
  status: selectNetworkListStatus,
  networks: selectNetworkList,
  me: selectMeBang,
  searchResults: selectNetworkSearchResults,
  currentNetworkId: selectCurrentNetworkId,
});

const actions = {
  loadNetworkList,
  searchNetworks,
};

export const ConnectedNetworkList = connect(selectors, actions)(NetworkList);
